import TwitchPlayer from "@/components/TwitchPlayer";
import { PLAYER_RUNESTONE_CABINET_QUERY } from "@/graphql/queries/profileQueries";
import { LIVE_STREAMS_SUBSCRIPTION } from "@/graphql/subscriptions/streamTeamSubscriptions";
import { useAuth } from "@/hooks/auth/useAuth";
import { convertLetters } from "@/utils/letterConversion";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
    ChevronLeft,
    ChevronRight,
    Instagram,
    Users,
    Youtube,
} from "lucide-react";
import { useEffect, useState } from "react";
import { FaDiscord, FaTiktok, FaTwitch } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { useSearchParams } from "react-router-dom";

const LIVE_STREAMS_QUERY = gql`
    query LiveStreams {
        liveStreams {
            id
            streamTeamMember {
                id
                player {
                    id
                    name
                    avatarUrl
                }
                twitchUsername
                bio
                displayOrder
                twitterUrl
                youtubeUrl
                discordUrl
                instagramUrl
                tiktokUrl
                twitchAuth {
                    profileImageUrl
                }
                runestones {
                    imageUrl
                }
            }
            isLive
            streamTitle
            gameName
            viewerCount
            startedAt
        }
    }
`;

interface LiveStream {
    id: string;
    streamTeamMember: {
        id: string;
        player: {
            id: string;
            name: string;
            avatarUrl: string | null;
        };
        twitchUsername: string;
        bio: string;
        displayOrder: number;
        twitterUrl: string;
        youtubeUrl: string;
        discordUrl: string;
        instagramUrl: string;
        tiktokUrl: string;
        twitchAuth?: {
            profileImageUrl: string;
        } | null;
        runestones?: { imageUrl: string }[];
    };
    isLive: boolean;
    streamTitle: string;
    gameName: string;
    viewerCount: number;
    startedAt: string;
}

interface StreamTeamMember {
    id: string;
    player: {
        id: string;
        name: string;
    };
    twitchUsername: string;
}

interface PlayerRunestoneCabinetEntry {
    redemptionCount: number;
    ravenguardPoints: number;
    streamTeamMember: {
        id: string;
    };
}

export default function StreamTeamLiveStreamsPage() {
    const [searchParams] = useSearchParams();
    const [currentStreamIndex, setCurrentStreamIndex] = useState(0);
    const [showSidebar, setShowSidebar] = useState(true);
    const { isAuthenticated } = useAuth();

    const { loading, error, data, refetch } = useQuery<{
        liveStreams: LiveStream[];
    }>(LIVE_STREAMS_QUERY, {
        fetchPolicy: "cache-and-network",
    });

    useSubscription(LIVE_STREAMS_SUBSCRIPTION, {
        onData: () => {
            refetch();
        },
    });

    const { data: viewerCabinetData, loading: viewerCabinetLoading } =
        useQuery<{ playerRunestoneCabinet: PlayerRunestoneCabinetEntry[] }>(
            PLAYER_RUNESTONE_CABINET_QUERY,
            {
                fetchPolicy: "cache-and-network",
                skip: !isAuthenticated,
            },
        );

    // Sort live streams by display order
    const liveStreams = [...(data?.liveStreams || [])].sort(
        (a, b) =>
            (a.streamTeamMember.displayOrder || 0) -
            (b.streamTeamMember.displayOrder || 0),
    );

    const currentStream = liveStreams[currentStreamIndex];
    const viewerProgress = currentStream
        ? viewerCabinetData?.playerRunestoneCabinet?.find(
              (entry) =>
                  entry.streamTeamMember.id ===
                  currentStream.streamTeamMember.id,
          )
        : undefined;

    // Set initial stream based on URL parameter
    useEffect(() => {
        const username = searchParams.get("username");
        if (username && liveStreams.length > 0) {
            const streamIndex = liveStreams.findIndex(
                (stream) => stream.streamTeamMember.twitchUsername === username,
            );
            if (streamIndex !== -1) {
                setCurrentStreamIndex(streamIndex);
            }
        }
    }, [searchParams, liveStreams]);

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 sm:py-12">
                <p className="font-garamond text-center text-zinc-400">
                    Loading live streams...
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="px-4 py-8 sm:px-6 sm:py-12">
                <p className="font-garamond text-center text-red-400">
                    Error loading live streams: {error.message}
                </p>
            </div>
        );
    }

    return (
        <div className="w-full px-4 py-8 sm:px-6 sm:py-12">
            <div className="mb-6 flex items-start justify-between gap-4 sm:mb-8">
                <div className="flex-1">
                    <h1 className="font-prviking text-brand mb-3 flex flex-wrap items-center gap-2 text-3xl sm:mb-4 sm:gap-4 sm:text-4xl lg:text-5xl">
                        {convertLetters("Live Streams")}
                        {liveStreams.length > 0 && (
                            <span className="font-garamond text-xl text-zinc-300 sm:text-2xl">
                                ({liveStreams.length})
                            </span>
                        )}
                    </h1>
                    <p className="font-garamond text-base text-zinc-300 sm:text-lg lg:text-xl">
                        Watch VRX Members live directly from the Rookery.
                    </p>
                    <p className="text-center text-zinc-400 italic">
                        Live Members are automatically filtered to the top. All
                        RAVENGUARD Members are otherwise ordered by Shield Value
                        (SV).
                    </p>
                </div>

                {/* Navigation arrows - only show when multiple streams */}
                {liveStreams.length > 1 && (
                    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                        <button
                            onClick={() =>
                                setCurrentStreamIndex((prev) =>
                                    prev === 0
                                        ? liveStreams.length - 1
                                        : prev - 1,
                                )
                            }
                            className="rounded-lg bg-zinc-700 p-2 text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-yellow-500 sm:p-3"
                            aria-label="Previous stream"
                        >
                            <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                        <div className="font-garamond hidden text-sm text-zinc-300 sm:block sm:text-base">
                            {currentStreamIndex + 1} / {liveStreams.length}
                        </div>
                        <button
                            onClick={() =>
                                setCurrentStreamIndex((prev) =>
                                    prev === liveStreams.length - 1
                                        ? 0
                                        : prev + 1,
                                )
                            }
                            className="rounded-lg bg-zinc-700 p-2 text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-yellow-500 sm:p-3"
                            aria-label="Next stream"
                        >
                            <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                    </div>
                )}
            </div>

            {liveStreams.length === 0 ? (
                <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 p-8 text-center sm:p-12">
                    <Users className="text-brand mx-auto mb-4 h-12 w-12 sm:h-16 sm:w-16" />
                    <h2 className="font-prviking text-brand mb-2 text-2xl sm:text-3xl">
                        {convertLetters("No Streams Live")}
                    </h2>
                    <p className="font-garamond text-sm text-zinc-400 sm:text-base">
                        No one is currently streaming. Check back soon or view
                        our schedule!
                    </p>
                </div>
            ) : (
                <LiveStreamEmbed
                    stream={currentStream}
                    showSidebar={showSidebar}
                    onToggleSidebar={() => setShowSidebar(!showSidebar)}
                    isAuthenticated={isAuthenticated}
                    viewerCabinetLoading={viewerCabinetLoading}
                    viewerProgress={viewerProgress}
                />
            )}
        </div>
    );
}

interface LiveStreamEmbedProps {
    stream: LiveStream;
    showSidebar: boolean;
    onToggleSidebar: () => void;
    isAuthenticated: boolean;
    viewerCabinetLoading: boolean;
    viewerProgress?: PlayerRunestoneCabinetEntry;
}

function LiveStreamEmbed({
    stream,
    showSidebar,
    onToggleSidebar,
    isAuthenticated,
    viewerCabinetLoading,
    viewerProgress,
}: LiveStreamEmbedProps) {
    const { streamTeamMember, streamTitle, gameName, viewerCount } = stream;

    const socialLinks = [
        {
            url: streamTeamMember.twitterUrl,
            icon: FaXTwitter,
            name: "X",
            color: "hover:text-blue-400",
        },
        {
            url: streamTeamMember.youtubeUrl,
            icon: Youtube,
            name: "YouTube",
            color: "hover:text-red-500",
        },
        {
            url: streamTeamMember.discordUrl,
            icon: FaDiscord,
            name: "Discord",
            color: "hover:text-indigo-400",
        },
        {
            url: streamTeamMember.instagramUrl,
            icon: Instagram,
            name: "Instagram",
            color: "hover:text-pink-400",
        },
        {
            url: streamTeamMember.tiktokUrl,
            icon: FaTiktok,
            name: "TikTok",
            color: "hover:text-cyan-400",
        },
    ].filter((link) => link.url);

    // Prefer Twitch profile image over player avatar
    const avatarUrl =
        streamTeamMember.twitchAuth?.profileImageUrl ||
        streamTeamMember.player.avatarUrl;

    return (
        <div className="border-brand_stream_team rounded-xl border-2 bg-zinc-900 shadow-xl">
            {/* Header */}
            <div className="border-brand_stream_team flex flex-col gap-3 border-b-2 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
                <div className="flex items-center gap-3 sm:gap-4">
                    {avatarUrl ? (
                        <img
                            src={avatarUrl}
                            alt={streamTeamMember.player.name}
                            className="h-10 w-10 rounded-full border-2 border-yellow-500 object-cover sm:h-12 sm:w-12"
                        />
                    ) : (
                        <div className="bg-brand flex h-10 w-10 items-center justify-center rounded-full text-lg font-bold text-zinc-900 sm:h-12 sm:w-12 sm:text-xl">
                            {streamTeamMember.player.name
                                .charAt(0)
                                .toUpperCase()}
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <h3 className="font-prviking text-brand truncate text-xl sm:text-2xl">
                            {convertLetters(streamTeamMember.player.name)}
                        </h3>
                        <p className="font-garamond text-xs text-zinc-400 sm:text-sm">
                            {viewerCount.toLocaleString()} viewers
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={onToggleSidebar}
                        className="font-garamond hidden rounded-lg bg-zinc-700 px-3 py-2 text-sm text-zinc-300 transition-colors hover:bg-zinc-600 hover:text-yellow-500 sm:px-4 lg:block"
                    >
                        {showSidebar ? "Hide Info" : "Show Info"}
                    </button>
                    <a
                        href={`https://www.twitch.tv/${streamTeamMember.twitchUsername}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-garamond flex items-center gap-2 rounded-lg border-2 border-purple-600 px-3 py-2 text-sm text-purple-400 transition-colors hover:bg-purple-600 hover:text-white sm:px-4"
                        aria-label="View on Twitch"
                    >
                        <FaTwitch className="h-4 w-4 sm:h-5 sm:w-5" />
                        <span className="hidden sm:inline">View on Twitch</span>
                        <span className="sm:hidden">Twitch</span>
                    </a>
                    <div className="bg-brand flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold text-zinc-900 sm:px-4 sm:py-2 sm:text-sm">
                        <span className="h-2 w-2 animate-pulse rounded-full bg-red-600"></span>
                        LIVE
                    </div>
                </div>
            </div>

            {/* Desktop layout - sidebar + video with chat */}
            <div className="hidden p-4 lg:block">
                <div className="flex gap-4">
                    {/* Sidebar */}
                    {showSidebar && (
                        <div className="w-80 shrink-0 space-y-4">
                            <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                                <h4 className="font-prviking text-brand mb-3 text-lg">
                                    {convertLetters("About")}
                                </h4>
                                <p className="font-garamond text-sm text-zinc-300">
                                    {streamTeamMember.bio ||
                                        "No bio available."}
                                </p>
                            </div>

                            {streamTeamMember.runestones &&
                                streamTeamMember.runestones.length > 0 && (
                                    <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4 text-center">
                                        <img
                                            src={
                                                streamTeamMember.runestones[0]
                                                    .imageUrl
                                            }
                                            alt="Runestone"
                                            className="mx-auto h-32 w-32 object-contain"
                                        />
                                    </div>
                                )}

                            {isAuthenticated && (
                                <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                                    <h4 className="font-prviking text-brand mb-2 text-lg">
                                        {convertLetters("Your Progress")}
                                    </h4>
                                    {viewerCabinetLoading ? (
                                        <p className="font-garamond text-sm text-zinc-400">
                                            Loading progress...
                                        </p>
                                    ) : (
                                        <div className="font-garamond space-y-1 text-sm text-zinc-300">
                                            <p>
                                                <span className="text-zinc-400">
                                                    RP:
                                                </span>{" "}
                                                <span className="font-semibold text-yellow-300">
                                                    {(
                                                        viewerProgress?.ravenguardPoints ??
                                                        0
                                                    ).toLocaleString()}
                                                </span>
                                            </p>
                                            <p>
                                                <span className="text-zinc-400">
                                                    Redemptions:
                                                </span>{" "}
                                                <span className="font-semibold text-yellow-300">
                                                    {(
                                                        viewerProgress?.redemptionCount ??
                                                        0
                                                    ).toLocaleString()}
                                                </span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {socialLinks.length > 0 && (
                                <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                                    <h4 className="font-prviking text-brand mb-3 text-lg">
                                        {convertLetters("Social Links")}
                                    </h4>
                                    <div className="space-y-2">
                                        {socialLinks.map((link) => (
                                            <a
                                                key={link.name}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex items-center gap-3 rounded-lg bg-zinc-800 p-3 text-zinc-400 transition-colors ${link.color}`}
                                            >
                                                <link.icon className="h-5 w-5" />
                                                <span className="font-garamond">
                                                    {link.name}
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                                <h4 className="font-prviking text-brand mb-2 text-lg">
                                    {convertLetters("Now Playing")}
                                </h4>
                                <h5 className="font-garamond mb-1 font-bold text-zinc-200">
                                    {streamTitle}
                                </h5>
                                <p className="font-garamond text-sm text-zinc-400">
                                    {gameName}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Twitch embed with chat */}
                    <div className="h-225 flex-1 overflow-hidden rounded-lg bg-zinc-900">
                        <TwitchPlayer
                            channel={streamTeamMember.twitchUsername}
                            autoplay={false}
                            muted={false}
                            layout="video-with-chat"
                            className="h-full w-full"
                        />
                    </div>
                </div>
            </div>

            {/* Mobile/Tablet layout - stacked vertically */}
            <div className="space-y-4 p-3 sm:p-4 lg:hidden">
                {/* Twitch video embed */}
                <div className="aspect-video w-full overflow-hidden rounded-lg bg-zinc-900">
                    <TwitchPlayer
                        channel={streamTeamMember.twitchUsername}
                        autoplay={false}
                        muted={false}
                        layout="video"
                        className="h-full w-full"
                    />
                </div>

                {/* Stream info */}
                <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                    <h4 className="font-prviking text-brand mb-2 text-lg">
                        {convertLetters("Now Playing")}
                    </h4>
                    <h5 className="font-garamond mb-1 font-bold text-zinc-200">
                        {streamTitle}
                    </h5>
                    <p className="font-garamond text-sm text-zinc-400">
                        {gameName}
                    </p>
                </div>

                {/* Twitch chat embed */}
                <div className="h-100 overflow-hidden rounded-lg bg-zinc-900 sm:h-125">
                    <iframe
                        src={`https://www.twitch.tv/embed/${streamTeamMember.twitchUsername}/chat?parent=${window.location.hostname}&darkpopout`}
                        className="h-full w-full"
                        title="Twitch Chat"
                    />
                </div>

                {/* About section */}
                <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                    <h4 className="font-prviking text-brand mb-3 text-lg">
                        {convertLetters("About")}
                    </h4>
                    <p className="font-garamond text-sm text-zinc-300">
                        {streamTeamMember.bio || "No bio available."}
                    </p>
                </div>

                {/* Runestone */}
                {streamTeamMember.runestones &&
                    streamTeamMember.runestones.length > 0 && (
                        <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4 text-center">
                            <img
                                src={streamTeamMember.runestones[0].imageUrl}
                                alt="Runestone"
                                className="mx-auto h-32 w-32 object-contain"
                            />
                        </div>
                    )}

                {isAuthenticated && (
                    <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                        <h4 className="font-prviking text-brand mb-2 text-lg">
                            {convertLetters("Your Progress")}
                        </h4>
                        {viewerCabinetLoading ? (
                            <p className="font-garamond text-sm text-zinc-400">
                                Loading progress...
                            </p>
                        ) : (
                            <div className="font-garamond space-y-1 text-sm text-zinc-300">
                                <p>
                                    <span className="text-zinc-400">RP:</span>{" "}
                                    <span className="font-semibold text-yellow-300">
                                        {(
                                            viewerProgress?.ravenguardPoints ??
                                            0
                                        ).toLocaleString()}
                                    </span>
                                </p>
                                <p>
                                    <span className="text-zinc-400">
                                        Redemptions:
                                    </span>{" "}
                                    <span className="font-semibold text-yellow-300">
                                        {(
                                            viewerProgress?.redemptionCount ?? 0
                                        ).toLocaleString()}
                                    </span>
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Social links */}
                {socialLinks.length > 0 && (
                    <div className="border-brand_stream_team rounded-lg border-2 bg-zinc-900 p-4">
                        <h4 className="font-prviking text-brand mb-3 text-lg">
                            {convertLetters("Social Links")}
                        </h4>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {socialLinks.map((link) => (
                                <a
                                    key={link.name}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-3 rounded-lg bg-zinc-800 p-3 text-zinc-400 transition-colors ${link.color}`}
                                >
                                    <link.icon className="h-5 w-5" />
                                    <span className="font-garamond">
                                        {link.name}
                                    </span>
                                </a>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
